import { ViewportSceneService } from '@/infrastructure/engine2d/scene/ViewportSceneService';
import type { Canvas } from 'fabric';
import type { FabricPortEventsService } from '@/infrastructure/engine2d/events/FabricPortEventsService';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { TFabricCustomSelectable } from '@/infrastructure/engine2d/types/TFabricCustomSelectable';
import type { IViewportEngine2D } from '@/application/ports/IViewportEngine2D';

type TFabricViewportEngine2DParams = {
    events: FabricPortEventsService;
    scene: FabricScene;
};

// Fabric adapter for IViewportEngine2D. Owns the viewport service
// (fit-to-view, focus on element) and mediates zoom / resize between
// the scene's canvas and the application layer. Zoom + after-render
// events are re-exposed from the shared FabricPortEventsService bus.
// Subscribes to scene.onResize to re-run focus / fit whenever the
// surface changes.
class FabricViewportEngine2D implements IViewportEngine2D {
    private readonly _canvas: Canvas;
    private readonly _scene: FabricScene;
    private readonly _events: FabricPortEventsService;
    private readonly _viewportScene: ViewportSceneService;
    private readonly _unsubscribeResize: () => void;

    constructor(params: TFabricViewportEngine2DParams) {
        this._scene = params.scene;
        this._events = params.events;
        this._canvas = params.scene.fabricCanvas;

        this._viewportScene = new ViewportSceneService({
            canvas: this._canvas,
            getCanvasHeight: () => this._scene.canvasHeight,
            getCanvasWidth: () => this._scene.canvasWidth,
            getDefaultZoom: () => this._scene.defaultZoomFactor,
            getImageRenderHeight: () => this._scene.imageRenderHeight,
            getImageRenderWidth: () => this._scene.imageRenderWidth,
        });

        this._unsubscribeResize = this._scene.onResize(() => this._handleResize());
    }

    dispose(): void {
        this._unsubscribeResize();
    }

    displayFullCanvasImage(): void {
        this._viewportScene.displayImageFullCanvas();
    }

    getCanvasClientRect(): { height: number; width: number } {
        const rect = this._canvas.getElement().getBoundingClientRect();
        return { height: rect.height, width: rect.width };
    }

    getViewportTransform(): { scaleX: number; scaleY: number; translateX: number; translateY: number } {
        const transform = this._canvas.viewportTransform;
        return {
            scaleX: transform[0],
            scaleY: transform[3],
            translateX: transform[4],
            translateY: transform[5],
        };
    }

    getZoom(): number {
        return this._canvas.getZoom();
    }

    onAfterRender(callback: () => void): () => void {
        return this._events.onAfterRender(callback);
    }

    onZoomChanged(callback: (zoom: number) => void): () => void {
        return this._events.onZoomChanged(callback);
    }

    resize(width: number, height: number): void {
        this._scene.resize(width, height);
    }

    getDefaultZoom(): number {
        return this._scene.defaultZoomFactor;
    }

    zoomTo(zoom: number): void {
        this._scene.zoomInOut(zoom);
    }

    private _handleResize(): void {
        const active = this._canvas.getActiveObject() as TFabricCustomSelectable | undefined;
        const zoom = this._canvas.getZoom();
        if (active && zoom) {
            this._viewportScene.focusOnElement(active);
        } else {
            this._viewportScene.displayImageFullCanvas();
        }
        this._events.dispatchZoomChanged(this._canvas.getZoom());
    }
}

export { FabricViewportEngine2D };
